//---------------------------------------------------------
// Pair detail (UX lock #51): header line, Nodes table on top, then the
// pair-wide settings tabs once for the pair with one Save to both nodes.
//---------------------------------------------------------
import {Box, Button, Snackbar, Tab, Tabs, TextField, Typography} from '@mui/material';
import {SoftAlert, SoftMono, SoftPage, SoftScope, SoftSection} from 'components/soft/SoftUI';
import {SOFT} from 'components/soft/softTheme';
import {getNodePower, getPair, getPairSettings, postNodePower, putPairSettings, resyncPairNode} from 'connector/oam/haPairApi';
import {useRole} from 'hooks/query/oamHooks';
import {useCallback, useEffect, useRef, useState} from 'react';
import {useParams} from 'react-router-dom';
import type {HANodeIndex, HAPairDetail, HAPairWideKind, HASettingsDoc} from 'types/haPair';
import {HA_PAIR_WIDE_KINDS} from 'types/haPair';
import HAConfirmDialog from './HAConfirmDialog';
import {HAChip} from './HAChips';
import HANodesTable from './HANodesTable';
import {
	CANT_TELL_VIP,
	failoverWarning,
	headerLine,
	HAPowerAction,
	powerDialog,
	PowerDialog,
	retryLabel,
	SAVE_LABEL,
	saveDisabled,
	saveOutcome,
	SaveOutcome,
	SAVING_LABEL,
	unreachableNode,
	unreachableWarning,
	vipHolder,
} from './haCopy';

export const HA_TAB_LABELS: Record<HAPairWideKind, string> = {
	loadbalancer: 'Load balancer',
	firewall: 'Firewall',
	endpoint: 'Endpoints',
	mirror: 'Mirror',
	policy: 'QoS policy',
};

export const HA_REFRESH_MS = 15000;

function docToText(doc: HASettingsDoc): Record<HAPairWideKind, string> {
	const out = {} as Record<HAPairWideKind, string>;
	for (const k of HA_PAIR_WIDE_KINDS) out[k] = JSON.stringify(doc[k] ?? [], null, 2);
	return out;
}

/** Parses the five tabs back into a settings doc, or names the bad tab. */
export function textToDoc(text: Record<HAPairWideKind, string>): {doc?: HASettingsDoc; error?: string} {
	const doc: HASettingsDoc = {};
	for (const k of HA_PAIR_WIDE_KINDS) {
		try {
			const v = JSON.parse(text[k] || '[]');
			if (!Array.isArray(v)) return {error: `${HA_TAB_LABELS[k]} must be a JSON list.`};
			doc[k] = v;
		} catch {
			return {error: `${HA_TAB_LABELS[k]} isn't valid JSON.`};
		}
	}
	return {doc};
}

function newKey(): string {
	const c = (globalThis as any).crypto;
	if (c?.randomUUID) return c.randomUUID();
	return `k-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

export default function HAPairPage(props: {pairId?: string}) {
	const params = useParams();
	const pairId = props.pairId ?? params.pairId ?? '';
	const {is_admin, can_write_gateway} = useRole();

	const [detail, setDetail] = useState<HAPairDetail | null>(null);
	const [loadErr, setLoadErr] = useState<string | null>(null);
	const [rev, setRev] = useState(0);
	const [text, setText] = useState<Record<HAPairWideKind, string> | null>(null);
	const [tab, setTab] = useState<HAPairWideKind>('loadbalancer');
	const [saving, setSaving] = useState(false);
	const [outcome, setOutcome] = useState<SaveOutcome | null>(null);
	const [toast, setToast] = useState<string | null>(null);
	const [formErr, setFormErr] = useState<string | null>(null);

	const [power, setPower] = useState<{node: HANodeIndex; action: HAPowerAction; dlg: PowerDialog} | null>(null);
	const [powerBusy, setPowerBusy] = useState(false);
	const [powerErr, setPowerErr] = useState<string | null>(null);
	const idemRef = useRef<{body: string; key: string} | null>(null);

	const load = useCallback(async () => {
		const r = await getPair(pairId);
		if (r.status === 200 && r.data) {
			setDetail(r.data);
			setLoadErr(null);
		} else setLoadErr(r.error?.message || 'Pair not found');
	}, [pairId]);

	const loadSettings = useCallback(async () => {
		const r = await getPairSettings(pairId);
		if (r.status === 200 && r.data) {
			setRev(r.data.settingsRev);
			setText(docToText(r.data.settings || {}));
		}
	}, [pairId]);

	useEffect(() => {
		load();
		loadSettings();
		const t = window.setInterval(load, HA_REFRESH_MS);
		return () => window.clearInterval(t);
	}, [load, loadSettings]);

	if (loadErr && !detail) {
		return (
			<SoftScope>
				<SoftPage title="Pair">
					<SoftAlert severity="error">{loadErr}</SoftAlert>
				</SoftPage>
			</SoftScope>
		);
	}
	if (!detail) return null;

	const holder = vipHolder(detail);
	const hdr = headerLine(detail);
	const warn = failoverWarning(detail);
	const unreach = unreachableNode(detail.nodes);
	const disabled = saveDisabled({saving, canWrite: can_write_gateway, nodes: detail.nodes, state: detail.state}) || !text;

	const onSave = async () => {
		if (!text) return;
		setFormErr(null);
		const parsed = textToDoc(text);
		if (!parsed.doc) {
			setFormErr(parsed.error || 'Invalid settings.');
			return;
		}
		const body = JSON.stringify(parsed.doc) + '|' + rev;
		if (!idemRef.current || idemRef.current.body !== body) idemRef.current = {body, key: newKey()};
		setSaving(true);
		setOutcome(null);
		const sent = rev;
		const r = await putPairSettings(pairId, sent, parsed.doc, idemRef.current.key);
		const o = saveOutcome(r, sent);
		setSaving(false);
		setOutcome(o);
		if (o.kind === 'saved') {
			setToast(o.toast);
			setRev(o.rev);
			idemRef.current = null;
		} else if (o.kind === 'partial' && o.rev != null) {
			setRev(o.rev);
			idemRef.current = null;
		}
		await load();
	};

	const onRetry = async (node: HANodeIndex) => {
		setSaving(true);
		const r = await resyncPairNode(pairId, node);
		setSaving(false);
		if (r.status === 200) setOutcome(null);
		else setOutcome({kind: 'partial', message: r.error?.message ? `${r.error.message.replace(/\.+$/, '')}.` : 'Retry failed.', retryNode: node});
		await load();
	};

	const openPower = async (node: HANodeIndex, action: HAPowerAction) => {
		setPowerErr(null);
		const pf = await getNodePower(pairId, node);
		const n = detail.nodes.find(x => x.node === node);
		const holdsVip = pf.data?.holdsVip ?? n?.holdsVip ?? null;
		const otherNodeDown = pf.status === 200 && pf.data ? pf.data.otherNodeDown : null;
		setPower({node, action, dlg: powerDialog({action, node, name: detail.name, holdsVip, otherNodeDown})});
	};

	const confirmPower = async () => {
		if (!power) return;
		setPowerBusy(true);
		const r = await postNodePower(pairId, power.node, power.action as 'boot' | 'shutdown' | 'reboot', power.dlg.confirmAllTrafficStops);
		setPowerBusy(false);
		if (r.status === 202 || r.status === 200) {
			setPower(null);
			await load();
			return;
		}
		if (r.status === 409 && r.error?.code === 'CONFIRM_REQUIRED') {
			// The other node went down since the preflight: show the stop-all copy.
			setPower({...power, dlg: powerDialog({action: power.action, node: power.node, name: detail.name, holdsVip: null, otherNodeDown: true})});
			return;
		}
		setPowerErr(r.error?.message ? `${r.error.message.replace(/\.+$/, '')}.` : 'Power request failed.');
	};

	const retryNode = outcome?.kind === 'partial' ? outcome.retryNode : null;

	return (
		<SoftScope>
			<SoftPage
				title={
					<Box display="flex" alignItems="center" gap="8px">
						<span>{detail.name}</span>
						<HAChip />
					</Box>
				}
			>
				<Box display="flex" gap="16px" alignItems="center" data-testid="pair-header" sx={{mb: 2, fontSize: 13, color: SOFT.color.textMuted}}>
					<SoftMono>{hdr.vip}</SoftMono>
					<span>{hdr.mode}</span>
					<span>{hdr.region}</span>
				</Box>
				{holder === null && (
					<Typography data-testid="cant-tell-vip" sx={{mb: 2, fontSize: 13, color: SOFT.color.textMuted}}>
						{CANT_TELL_VIP}
					</Typography>
				)}
				{warn && (
					<SoftAlert severity="warning" data-testid="failover-warning" sx={{mb: 2}}>
						{warn}
					</SoftAlert>
				)}

				<SoftSection title="Nodes">
					<HANodesTable
						nodes={detail.nodes}
						holder={holder}
						saving={saving}
						canPower={is_admin}
						canRetry={can_write_gateway}
						onPower={openPower}
						onRetry={onRetry}
					/>
				</SoftSection>

				<SoftSection title="Settings">
					{unreach !== null && (
						<SoftAlert severity="warning" data-testid="unreachable-warning" sx={{mb: 2}}>
							{unreachableWarning(unreach)}
						</SoftAlert>
					)}
					{outcome && outcome.kind !== 'saved' && !(outcome.kind === 'unreachable' && unreach !== null) && (
						<SoftAlert
							severity={outcome.kind === 'unreachable' ? 'warning' : 'error'}
							data-testid="save-alert"
							sx={{mb: 2}}
							action={
								retryNode && can_write_gateway ? (
									<Button color="inherit" size="small" onClick={() => onRetry(retryNode)} disabled={saving} data-testid="alert-retry">
										{retryLabel(retryNode)}
									</Button>
								) : undefined
							}
						>
							{outcome.message}
						</SoftAlert>
					)}
					{formErr && (
						<SoftAlert severity="error" sx={{mb: 2}}>
							{formErr}
						</SoftAlert>
					)}
					<Tabs value={tab} onChange={(_, v) => setTab(v)} aria-label="Pair settings">
						{HA_PAIR_WIDE_KINDS.map(k => (
							<Tab key={k} value={k} label={HA_TAB_LABELS[k]} />
						))}
					</Tabs>
					<TextField
						multiline
						minRows={12}
						fullWidth
						value={text?.[tab] ?? ''}
						onChange={e => text && setText({...text, [tab]: e.target.value})}
						slotProps={{htmlInput: {'aria-label': `${HA_TAB_LABELS[tab]} settings`, readOnly: saving || !can_write_gateway, 'data-testid': 'settings-editor'}}}
						sx={{mt: 2, '& textarea': {fontFamily: SOFT.font.mono, fontSize: 12}}}
					/>
					<Box display="flex" justifyContent="flex-end" sx={{mt: 2}}>
						<Button variant="contained" onClick={onSave} disabled={disabled} data-testid="pair-save">
							{saving ? SAVING_LABEL : SAVE_LABEL}
						</Button>
					</Box>
				</SoftSection>

				<Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)} message={toast ?? ''} data-testid="save-toast" />
				{power && (
					<HAConfirmDialog
						open
						title={power.dlg.title}
						body={power.dlg.body}
						confirm={power.dlg.confirm}
						destructive={power.dlg.destructive}
						busy={powerBusy}
						error={powerErr}
						onConfirm={confirmPower}
						onClose={() => setPower(null)}
					/>
				)}
			</SoftPage>
		</SoftScope>
	);
}
